import React, { useEffect, useRef, useState } from 'react';
import { Alert, Button, Form, Input, Space, Typography } from 'antd';
import { BUYER_ACCOUNT_API, SaleBuyerSession, SaleCodeDelivery, saleRequest } from '../../services/saleLinks';
import { saleDate, saleError, useSaleText } from './saleUi';

interface Props { session: SaleBuyerSession; onChange: (session: SaleBuyerSession) => void; disabled?: boolean }

const SaleAccountLogin: React.FC<Props> = ({ session, onChange, disabled = false }) => {
  const text = useSaleText();
  const [form] = Form.useForm();
  const [delivery, setDelivery] = useState<(SaleCodeDelivery & { email: string }) | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [seconds, setSeconds] = useState(0);
  const deadline = useRef(0);
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    const timer = window.setInterval(() => setSeconds(Math.max(0, Math.ceil((deadline.current - Date.now()) / 1000))), 1000);
    return () => { mounted.current = false; window.clearInterval(timer); };
  }, []);

  const send = async () => {
    if (busy || seconds > 0 || disabled) return;
    let email: string;
    try { email = (await form.validateFields(['email'])).email.trim().toLowerCase(); } catch { return; }
    setBusy(true); setError('');
    try {
      const result = await saleRequest<SaleCodeDelivery>(`${BUYER_ACCOUNT_API}/send-code`, 'POST', { email });
      if (!mounted.current) return;
      setDelivery({ ...result, email }); form.setFieldValue('code', '');
      deadline.current = Date.now() + result.retryAfterSeconds * 1000; setSeconds(result.retryAfterSeconds);
    } catch (failure) { if (mounted.current) setError(saleError(failure, text)); }
    finally { if (mounted.current) setBusy(false); }
  };

  const verify = async ({ email, code }: { email: string; code: string }) => {
    if (!delivery || busy || disabled || delivery.email !== email.trim().toLowerCase()) return;
    setBusy(true); setError('');
    try {
      const next = await saleRequest<SaleBuyerSession>(`${BUYER_ACCOUNT_API}/verify-code`, 'POST', {
        email: delivery.email, code, challengeId: delivery.challengeId,
      });
      if (mounted.current) { setDelivery(null); form.resetFields(); onChange(next); }
    } catch (failure) { if (mounted.current) setError(saleError(failure, text)); }
    finally { if (mounted.current) setBusy(false); }
  };

  const logout = async () => {
    if (busy || disabled) return;
    setBusy(true); setError('');
    try {
      await saleRequest(`${BUYER_ACCOUNT_API}/logout`, 'POST', {});
      if (mounted.current) onChange({ ...session, authenticated: false, email: null, expiresAt: null });
    } catch (failure) { if (mounted.current) setError(saleError(failure, text)); }
    finally { if (mounted.current) setBusy(false); }
  };

  return <div className="sale-account-login">
    {error && <Alert type="error" showIcon message={error} />}
    {session.authenticated ? <Space direction="vertical" size="small">
      <Typography.Text>{text('当前买家账号：', 'Buyer account: ')}{session.email}</Typography.Text>
      <Typography.Text type="secondary">{text('登录有效至：', 'Signed in until: ')}{saleDate(session.expiresAt)}</Typography.Text>
      <Button onClick={() => void logout()} loading={busy} disabled={disabled}>{text('退出 / 切换账号', 'Sign out / switch account')}</Button>
    </Space> : session.accountEnabled ? <>
      <Typography.Paragraph type="secondary">{text('通过邮箱验证码登录。跨设备使用时，请登录同一个邮箱账号。', 'Sign in with an email code. Use the same email on other devices.')}</Typography.Paragraph>
      <Form form={form} layout="vertical" onFinish={verify} disabled={busy || disabled}
        onValuesChange={(changed) => { if ('email' in changed) { setDelivery(null); form.setFieldValue('code', ''); } }}>
        <Form.Item name="email" label={text('邮箱', 'Email')} rules={[{ required: true, type: 'email', message: text('请输入有效邮箱', 'Enter a valid email') }]}>
          <Input type="email" autoComplete="email" maxLength={254} />
        </Form.Item>
        <Button onClick={() => void send()} disabled={busy || disabled || seconds > 0} loading={busy && !delivery}>
          {seconds > 0 ? text(`${seconds} 秒后重发`, `Resend in ${seconds}s`) : text('发送邮箱验证码', 'Send email code')}
        </Button>
        {delivery && <>
          <Typography.Paragraph type="secondary" className="sale-code-sent">{text('验证码已发送，请检查收件箱和垃圾邮件。', 'Code sent. Check your inbox and spam folder.')}</Typography.Paragraph>
          <Form.Item name="code" label={text('邮箱验证码', 'Email verification code')} rules={[{ required: true, pattern: /^\d{6}$/, message: text('请输入 6 位验证码', 'Enter the 6-digit code') }]}>
            <Input inputMode="numeric" autoComplete="one-time-code" maxLength={6} />
          </Form.Item>
          <Button type="primary" htmlType="submit" loading={busy}>{text('验证并登录', 'Verify and sign in')}</Button>
        </>}
      </Form>
    </> : <Alert type="info" message={text('邮箱账号登录暂未开放，已有设备授权仍可使用。', 'Email sign-in is currently closed. Existing device access remains available.')} />}
  </div>;
};
export default SaleAccountLogin;
